import { db } from "../firebase-config";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";

/* Utility: chunk an array (for Firestore 'in' limit of 10 values) */
function chunk(arr, size = 10) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/**
 * Fetch batches by batchId with safe chunking.
 * Returns plain array of { id, ...data }.
 */
async function fetchBatchesByIds(batchIds = []) {
  if (!Array.isArray(batchIds) || batchIds.length === 0) return [];
  const col = collection(db, "lms_batch");
  const chunks = chunk(batchIds.filter(Boolean));
  const all = [];
  for (const ids of chunks) {
    const qy = query(col, where("batchId", "in", ids));
    const snap = await getDocs(qy);
    snap.docs.forEach((d) => all.push({ id: d.id, ...d.data() }));
  }
  return all;
}

/**
 * Keep only batches where at least one module is assigned to this trainer.
 * Also strip moduleSettings to only those entries that belong to this trainer.
 */
function filterBatchesForTrainer(batches = [], trainerId) {
  return batches
    .map((b) => {
      const ms = b?.moduleSettings || {};
      const trainerEntries = Object.entries(ms).filter(
        ([, s]) => s?.moduleTrainerId === trainerId
      );
      if (trainerEntries.length === 0) return null;
      return { ...b, moduleSettings: Object.fromEntries(trainerEntries) };
    })
    .filter(Boolean);
}

/**
 * Build the UI-friendly package list that a trainer can see.
 * - Input `pkgs`: the same array you currently pass as `data` prop
 * - Output: [{ ...pkg, batches: [filtered only to trainer's modules] }]
 */
export async function buildPackagesForTrainer(pkgs = [], trainerId) {
  const result = [];
  for (const pkg of pkgs) {
    const batchIds = Array.isArray(pkg?.packageContainingBatches)
      ? pkg.packageContainingBatches
      : [];
    if (batchIds.length === 0) {
      result.push({ ...pkg, batches: [] });
      continue;
    }
    const batches = await fetchBatchesByIds(batchIds);
    const filtered = filterBatchesForTrainer(batches, trainerId);
    if (filtered.length > 0) {
      result.push({ ...pkg, batches: filtered });
    }
  }
  return result;
}

/* =========================
   Module meta (lms_module)
   ========================= */

/** Read current module meta */
export async function getModuleMeta(moduleId) {
  if (!moduleId) throw new Error("getModuleMeta: moduleId is required");
  const ref = doc(db, "lms_module", moduleId);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}

/** Update liveLink / isCompleted (+ completedAt) */
export async function updateModuleMeta({ moduleId, liveLink, isCompleted }) {
  if (!moduleId) throw new Error("updateModuleMeta: moduleId is required");
  const ref = doc(db, "lms_module", moduleId);

  const payload = { updatedAt: serverTimestamp() };
  if (typeof liveLink === "string") payload.liveLink = liveLink.trim() || null;
  if (typeof isCompleted === "boolean") {
    payload.isCompleted = isCompleted;
    payload.completedAt = isCompleted ? serverTimestamp() : null;
  }

  await updateDoc(ref, payload);
}
